import { LOCATION_INITIALIZED } from '@angular/common';
import { Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_APP_LANGUAGE, DEFAULT_PARTNER_LANGUAGE } from 'app/app.contants';
import { LocalStorageService } from './local-storage.service';
import { UserTrackingService } from './user-tracking.service';
import { WhiteLabelClientSettingsService } from './white-label-client-settings.service';

@Injectable({
	providedIn: 'root',
})
export class LanguageInitializerService {
	constructor(
		private translate: TranslateService,
		private injector: Injector,
		private localStorage: LocalStorageService,
		private userTrackingService: UserTrackingService,
		private clientSettingsService: WhiteLabelClientSettingsService
	) {}

	readonly availableLangs = ['sv', 'en', 'fi', 'da', 'nb', 'de', 'fr'];

	//used in app.module to initialize the language
	initLanguage(): Promise<void> {
		return new Promise<void>((resolve: () => void) => {
			const locationInitialized = this.injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
			locationInitialized.then(() => {
				const defaultLang = DEFAULT_APP_LANGUAGE;
				this.translate.addLangs(this.availableLangs);

				// If we're on a white-label route, defer language selection to the partner initializer
				// and avoid writing a global default (sv). Prefer the partner's default language as fallback.
				const isWhiteLabelRoute = window.location.pathname.split('/')[1] === 'p';
				if (isWhiteLabelRoute) {
					const existingSettings = this.clientSettingsService.getSettings();
					if (existingSettings?.defaultLanguage) {
						this.setFallbackLanguage(existingSettings.defaultLanguage);
						resolve();
						return;
					}
					const partnerKey = window.location.pathname.split('/')[2];
					this.clientSettingsService.loadPartnerSettings(partnerKey).subscribe(
						settings => {
							if (!settings || !settings.defaultLanguage) {
								console.error('Partner settings not available or missing defaultLanguage for key:', partnerKey);
								this.setFallbackLanguage(DEFAULT_PARTNER_LANGUAGE);
								resolve();
								return;
							}
							this.setFallbackLanguage(settings.defaultLanguage);
							resolve();
						},
						error => {
							console.error('Failed to load partner settings for key:', partnerKey, error);
							this.setFallbackLanguage(DEFAULT_PARTNER_LANGUAGE);
							resolve();
						}
					);
					return;
				}

				this.setFallbackLanguage(defaultLang);

				const cacheKey = 'chosenLocale';

				const queryParamLocale = this.getLocaleFromQueryParam();
				const cachedLang = this.localStorage.get(cacheKey);
				const langToSet = queryParamLocale ? queryParamLocale : cachedLang ? cachedLang : defaultLang;

				this.translate.use(langToSet).subscribe(
					() => {
						console.info(`Successfully initialized '${langToSet}' language.'`);
						this.localStorage.set(cacheKey, langToSet);
						this.userTrackingService.setUserVars({ locale: langToSet });
					},
					error => {
						console.error(`Problem with '${langToSet}' language initialization. `, error);
					},
					() => {
						resolve();
					}
				);
			});
		});
	}

	//used in white-label-client.module to initialize the language
	initPartnerLanguage(): Promise<void> {
		return new Promise<void>((resolve: () => void) => {
			this.setFallbackLanguage(DEFAULT_PARTNER_LANGUAGE);
			const partnerSettings = this.clientSettingsService.getSettings();
			const defaultLang = partnerSettings?.defaultLanguage;
			const cachedLang = this.localStorage.get('chosenLocale');
			const supportedLanguages = partnerSettings.supportedLanguages;

			let langToSet = cachedLang;
			if (!supportedLanguages.find(lang => lang.key === cachedLang) || !cachedLang) {
				langToSet = defaultLang;
			}

			const queryLocale = this.getLocaleFromQueryParam();
			if (queryLocale) {
				// Normalize the locale code to match the format in supportedLanguages
				const normalizedQueryLocale = queryLocale.split('-')[0];
				if (supportedLanguages.find(lang => lang.key === normalizedQueryLocale)) {
					langToSet = normalizedQueryLocale;
				}
			}

			this.translate.use(langToSet).subscribe(
				() => {
					console.info(`Successfully initialized '${langToSet}' language.'`);
					this.localStorage.set('chosenLocale', langToSet);
					this.userTrackingService.setUserVars({ locale: langToSet });
				},
				error => {
					console.error(`Problem with '${langToSet}' language initialization. `, error);
				},
				() => {
					resolve();
				}
			);
		});
	}

	private setFallbackLanguage(fallbackLanguage: string) {
		//use as fallback language, for translations that are not available in the selected language
		this.translate.setDefaultLang(fallbackLanguage);
	}

	private getLocaleFromQueryParam() {
		let queryParamLocale;
		const url = new URL(window.location.href);
		const locale = url.searchParams.get('locale');
		if (locale) {
			// Extract just the language part (e.g., 'en' from 'en-GB')
			const normalizedLocale = locale.split('-')[0];
			if (this.availableLangs.indexOf(normalizedLocale) > -1) {
				queryParamLocale = normalizedLocale;
			}
		}
		return queryParamLocale;
	}
}
